import React from 'react';
import {
  Box,
  Typography,
  TableCell,
  TableRow,
  IconButton,
  Collapse,
  Chip,
  Grid,
  useTheme,
  styled,
  Link,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import JsonViewer from 'shared/JsonViewer';

import {
  ActivityActionType,
  ActivityActionTypeLabels,
  ActivityLogItem,
  ActivityLogSourceLabels,
} from 'ActivityLogModule/types/activityLog';
import {
  getDisplayTeamId,
  getImpersonationTargetAccountId,
} from 'ActivityLogModule/utils/activityLogDetails';

const ExpandedContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.6)' : 'rgba(248, 250, 252, 0.8)',
  border: '1px solid',
  borderColor:
    theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.12)' : 'rgba(226, 232, 240, 0.6)',
  borderRadius: 0, // Sharp corners to match other tables
  margin: theme.spacing(0, 2, 2, 2),
}));

// Cast restores Link's overridable `component` prop, which styled() strips.
const TeamIdLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  cursor: 'pointer',
  fontWeight: 500,
  '&:hover': {
    textDecoration: 'underline',
  },
})) as typeof Link;

export interface TeamNavigationArgs {
  teamId: string;
  tenant?: string;
  realmId?: string;
}

export interface ActivityLogExpandableRowProps {
  log: ActivityLogItem;
  isExpanded: boolean;
  onToggle: () => void;
  onTeamClick?: (args: TeamNavigationArgs) => void;
}

export const ActivityLogExpandableRow: React.FC<ActivityLogExpandableRowProps> = ({
  log,
  isExpanded,
  onToggle,
  onTeamClick,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // GLX-1789: rows logged before GLX-1255 have no teamId column value, but the
  // captured request in `details` may still identify the team; impersonation
  // rows carry the target account only inside `details.body`.
  const displayTeamId = getDisplayTeamId(log);
  const impersonationTargetAccountId = getImpersonationTargetAccountId(log);

  const handleTeamClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (displayTeamId && onTeamClick) {
      // Pass the row's own tenant/realm: the log list spans tenants, and the
      // team id is only meaningful in the realm the request was made against.
      onTeamClick({ teamId: displayTeamId, tenant: log.tenant, realmId: log.realmId });
    }
  };

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            size="small"
            onClick={onToggle}
            aria-label={isExpanded ? 'Collapse row details' : 'Expand row details'}
            aria-expanded={isExpanded}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{log.occurredAt ? new Date(log.occurredAt).toLocaleString() : '-'}</TableCell>
        <TableCell>{log.username || '-'}</TableCell>
        <TableCell>{log.email || '-'}</TableCell>
        <TableCell>
          <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
            {log.actionType ? (
              <Chip
                label={
                  ActivityActionTypeLabels[log.actionType as ActivityActionType] || log.actionType
                }
                size="small"
                color="primary"
                sx={{ fontWeight: 500 }}
              />
            ) : (
              '-'
            )}
            {log.source === 'mcp' && (
              <Chip
                label={ActivityLogSourceLabels.mcp}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell>
          {log.summary || '-'}
          {impersonationTargetAccountId && (
            <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
              Impersonation target account ID: {impersonationTargetAccountId}
            </Typography>
          )}
        </TableCell>
        <TableCell>{log.tenant || '-'}</TableCell>
        <TableCell>{log.realmId || '-'}</TableCell>
        <TableCell>
          {displayTeamId ? (
            // component="button" keeps the JS-triggered navigation keyboard
            // focusable/activatable; an anchor without href is neither.
            <TeamIdLink component="button" type="button" onClick={handleTeamClick}>
              {displayTeamId}
            </TeamIdLink>
          ) : (
            '-'
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <ExpandedContent>
              <Grid container spacing={3}>
                <Grid size={12}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                      Access Level:
                    </Typography>
                    {log.accessLevel ? (
                      <Chip
                        label={log.accessLevel}
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 500 }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        -
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {impersonationTargetAccountId && (
                  <Grid size={12}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                        Target Account ID (from request):
                      </Typography>
                      <Typography variant="body2">{impersonationTargetAccountId}</Typography>
                    </Box>
                  </Grid>
                )}

                <Grid size={12}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
                    Details:
                  </Typography>
                  {log.details ? (
                    <Box
                      sx={{
                        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '8px',
                        padding: 1.5,
                      }}
                    >
                      <JsonViewer
                        src={log.details}
                        displayDataTypes={false}
                        displayObjectSize={false}
                        enableClipboard={true}
                        collapsed={1}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      -
                    </Typography>
                  )}
                </Grid>

                <Grid size={12}>
                  <Box display="flex" gap={4}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                        Network Log ID:
                      </Typography>
                      <Typography variant="body2">{log.networkLogId || '-'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                        Action Log ID:
                      </Typography>
                      <Typography variant="body2">{log.actionLogId || '-'}</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </ExpandedContent>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
